import mapValues from 'lodash/mapValues';
import type { Persona } from './Personas';
import { sumPoints } from './Score';
import type { PersonaScores, Score } from './Score';

export type Reaction = 'positive' | 'negative';

type PersonaReactionsData = {
	[personaName: string]: Reaction | undefined;
};

class PersonaReactions {
	public readonly data: PersonaReactionsData;

	private constructor(data: PersonaReactionsData) {
		this.data = data;
	}

	public getFor(persona: Persona): Reaction | undefined {
		return this.data[persona.name];
	}

	public hasFor(persona: Persona): boolean {
		return this.getFor(persona) !== undefined;
	}

	/**
	 * @param defaultReaction default reaction for undefined reaction values
	 */
	public hasChangedFor(
		personaReactions: PersonaReactions,
		persona: Persona,
		defaultReaction?: Reaction
	): boolean {
		return (
			(this.getFor(persona) ?? defaultReaction) !==
			(personaReactions.getFor(persona) ?? defaultReaction)
		);
	}

	private static isPositive = (score: Score): boolean => {
		return sumPoints(score) >= 0;
	};

	private static getReaction(score?: Score): Reaction | undefined {
		if (score === undefined) {
			return undefined;
		}
		return PersonaReactions.isPositive(score) ? 'positive' : 'negative';
	}

	public static fromScores(personaScores: PersonaScores): PersonaReactions {
		return new PersonaReactions(
			mapValues(personaScores, PersonaReactions.getReaction)
		);
	}
}

export { PersonaReactions };
